import { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  MessageSquare,
  Send,
  Search,
  Plus,
  ChevronLeft,
  Clock,
  Check,
  CheckCheck,
  Paperclip,
  AlertCircle
} from 'lucide-react'
import api from '../config/api'
import { useAuth } from '../hooks/useAuth'
import { useBranding } from '../hooks/useBranding'
import { useRealtimeMessages } from '../hooks/useRealtimeMessages'
import '../styles/Messages.css'

function Messages() {
  const { client } = useAuth()
  const { branding } = useBranding()
  const firmName = branding?.firmName || 'Your Legal Team'
  const [conversations, setConversations] = useState([])
  const [activeThread, setActiveThread] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingThread, setLoadingThread] = useState(false)
  const [error, setError] = useState('')
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [composing, setComposing] = useState(false)
  const [newSubject, setNewSubject] = useState('')
  const messagesEndRef = useRef(null)
  const typingTimeoutRef = useRef(null)

  // Real-time messaging (Firestore listener)
  const tenantId = client?.tenantId
  const { realtimeMessages, typingUsers, unreadCount, setTyping } = useRealtimeMessages(client?.id, tenantId)

  // Auto-refresh conversations when realtime messages change
  useEffect(() => {
    if (realtimeMessages.length > 0) {
      fetchConversations()
      // If viewing a thread, update messages in real-time
      if (activeThread) {
        const threadMsgs = realtimeMessages.filter(
          m => m.threadId === activeThread || m.id === activeThread
        )
        if (threadMsgs.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id))
            const newMsgs = threadMsgs.filter(m => !existingIds.has(m.id))
            return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev
          })
        }
      }
    }
  }, [realtimeMessages])

  useEffect(() => {
    fetchConversations()
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Typing indicator on input change
  const handleInputChange = useCallback((e) => {
    setNewMessage(e.target.value)
    setTyping(true)
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => setTyping(false), 3000)
  }, [setTyping])

  const fetchConversations = async () => {
    try {
      setLoading(true)
      const response = await api.get('/messages')
      setConversations(response.data.data)
    } catch (err) {
      setError('Failed to load messages')
    } finally {
      setLoading(false)
    }
  }

  const fetchThread = async (threadId) => {
    try {
      setLoadingThread(true)
      const response = await api.get(`/messages/thread/${threadId}`)
      setMessages(response.data.data)
      setActiveThread(threadId)
      setComposing(false)
    } catch (err) {
      setError('Failed to load conversation')
    } finally {
      setLoadingThread(false)
    }
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim()) return

    try {
      setSending(true)
      const payload = {
        message: newMessage,
        ...(composing ? { subject: newSubject || 'New message' } : { threadId: activeThread })
      }

      await api.post('/messages', payload)
      setNewMessage('')

      if (composing) {
        setComposing(false)
        setNewSubject('')
        fetchConversations()
      } else {
        fetchThread(activeThread)
      }
    } catch (err) {
      setError('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return ''

    let date
    // Handle Firestore Timestamp format (serialized as {_seconds, _nanoseconds})
    if (timestamp._seconds) {
      date = new Date(timestamp._seconds * 1000)
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000)
    } else if (timestamp.toDate) {
      date = timestamp.toDate()
    } else {
      date = new Date(timestamp)
    }

    // Check for invalid date
    if (isNaN(date.getTime())) return ''

    const now = new Date()
    const diff = now - date

    if (diff < 86400000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    if (diff < 604800000) {
      return date.toLocaleDateString([], { weekday: 'short' })
    }
    return date.toLocaleDateString()
  }

  return (
    <div className="messages-page">
      <div className={`conversations-panel ${activeThread || composing ? 'hidden-mobile' : ''}`}>
        <div className="panel-header">
          <h2>Messages</h2>
          <button
            className="btn btn-icon new-message-btn"
            onClick={() => {
              setComposing(true)
              setActiveThread(null)
              setMessages([])
            }}
            title="New Message"
          >
            <Plus size={20} />
          </button>
        </div>

        <div className="conversations-list">
          {loading ? (
            <div className="loading-container small">
              <div className="loading-spinner" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="empty-conversations">
              <MessageSquare size={32} />
              <p>No messages yet</p>
              <button
                className="btn btn-primary"
                onClick={() => setComposing(true)}
              >
                Message {firmName}
              </button>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id || conv.threadId}
                className={`conversation-item ${activeThread === conv.threadId ? 'active' : ''} ${conv.hasUnread ? 'unread' : ''}`}
                onClick={() => fetchThread(conv.threadId || conv.id)}
              >
                <div className="conversation-avatar">
                  {firmName[0]}
                </div>
                <div className="conversation-content">
                  <div className="conversation-header">
                    <span className="conversation-subject">{firmName}</span>
                    <span className="conversation-time">{formatTime(conv.createdAt)}</span>
                  </div>
                  <p className="conversation-preview">
                    {conv.subject || conv.message?.substring(0, 60)}
                  </p>
                </div>
                {conv.hasUnread && <span className="unread-badge" />}
              </div>
            ))
          )}
        </div>
      </div>

      <div className={`messages-panel ${!activeThread && !composing ? 'hidden-mobile' : ''}`}>
        {!activeThread && !composing ? (
          <div className="no-conversation">
            <MessageSquare size={48} />
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the list or start a new one</p>
          </div>
        ) : composing ? (
          <div className="compose-view">
            <div className="panel-header">
              <button
                className="btn btn-icon back-btn"
                onClick={() => setComposing(false)}
              >
                <ChevronLeft size={20} />
              </button>
              <h3>Message to {firmName}</h3>
            </div>
            <div className="compose-form">
              <input
                type="text"
                placeholder="Subject"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                className="subject-input"
              />
              <form onSubmit={handleSendMessage} className="message-form">
                <textarea
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={6}
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={sending || !newMessage.trim()}
                >
                  {sending ? 'Sending...' : 'Send Message'}
                  <Send size={18} />
                </button>
              </form>
            </div>
          </div>
        ) : (
          <>
            <div className="panel-header">
              <button
                className="btn btn-icon back-btn mobile-only"
                onClick={() => setActiveThread(null)}
              >
                <ChevronLeft size={20} />
              </button>
              <h3>{firmName}</h3>
              <span style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: 8 }}>
                {conversations.find(c => c.threadId === activeThread || c.id === activeThread)?.subject || ''}
              </span>
            </div>

            <div className="messages-container">
              {loadingThread ? (
                <div className="loading-container">
                  <div className="loading-spinner" />
                </div>
              ) : (
                <>
                  {messages.map((msg, index) => (
                    <motion.div
                      key={msg.id}
                      className={`message ${msg.direction === 'inbound' ? 'sent' : 'received'}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="message-content">
                        <p>{msg.message}</p>
                        <div className="message-meta">
                          <span className="message-time">{formatTime(msg.createdAt)}</span>
                          {msg.direction === 'inbound' && (
                            <span className={`message-status ${msg.isRead ? 'read' : ''}`}>
                              {msg.isRead ? <CheckCheck size={12} /> : <Check size={12} />}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {typingUsers.length > 0 && (
              <div className="typing-indicator">
                <span className="typing-dots">
                  <span /><span /><span />
                </span>
                <span className="typing-text">
                  {typingUsers.map(u => u.name).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </span>
              </div>
            )}

            <form onSubmit={handleSendMessage} className="reply-form">
              <input
                type="text"
                placeholder="Type a reply..."
                value={newMessage}
                onChange={handleInputChange}
              />
              <button
                type="submit"
                className="btn btn-primary btn-icon"
                disabled={sending || !newMessage.trim()}
              >
                <Send size={18} />
              </button>
            </form>
          </>
        )}
      </div>

      {error && (
        <div className="alert alert-error floating">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')}>&times;</button>
        </div>
      )}
    </div>
  )
}

export default Messages
